import { useCallback, useEffect, useRef, useState } from "react";
import { Map, NavigationControl, GeolocateControl } from "react-map-gl/maplibre";
import type { MapRef, ViewStateChangeEvent } from "react-map-gl/maplibre";
import { useEditorStore } from "@/store";
import { MAP_DEFAULTS } from "@/styles/theme";
import { useMapHighlight } from "@/hooks/useMapHighlight";
import { useInitialViewOverlay } from "./InitialViewOverlay";
import { MonitorPanel } from "./MonitorPanel";
import { onFlyToInitialView, onFlyToZoom } from "@/lib/mapCommands";
import { setGlobalMapRef } from "@/hooks/useFeatureProperties";

const MAX_PITCH = 85;

export function MapPreview() {
  const mapRef = useRef<MapRef>(null);
  const compareMapRef = useRef<MapRef>(null);
  const [showMonitor, setShowMonitor] = useState(true);
  setGlobalMapRef(mapRef);
  const showComparison = useEditorStore((s) => s.showComparison);
  const originalStyle = useEditorStore((s) => s.originalStyle);
  const syncing = useRef(false);
  useMapHighlight(mapRef);
  useInitialViewOverlay(mapRef);
  const style = useEditorStore((s) => s.style);
  const styleVersion = useEditorStore((s) => s.styleVersion);
  const viewport = useEditorStore((s) => s.viewport);
  const setViewport = useEditorStore((s) => s.setViewport);
  const setSelectedLayerId = useEditorStore((s) => s.setSelectedLayerId);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);
  const setActiveSection = useEditorStore((s) => s.setActiveSection);
  const lastAppliedVersion = useRef(0);

  // Apply style changes via diff mode for efficiency.
  // Skip the first render — the mapStyle prop already handles the initial style.
  // Only call setStyle for subsequent edits.
  const isFirstRender = useRef(true);
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || styleVersion === lastAppliedVersion.current) return;
    lastAppliedVersion.current = styleVersion;

    if (isFirstRender.current) {
      // First render: mapStyle prop already applied the style.
      // Just consume pendingFlyTo after the map loads.
      isFirstRender.current = false;
      const pending = useEditorStore.getState().pendingFlyTo;
      if (pending) {
        useEditorStore.setState({ pendingFlyTo: null });
        const applyFlyTo = () => {
          map.jumpTo({
            center: pending.center,
            zoom: pending.zoom,
            pitch: pending.pitch,
            bearing: pending.bearing,
          });
        };
        if (map.loaded()) {
          applyFlyTo();
        } else {
          map.once("load", applyFlyTo);
        }
      }
      return;
    }

    // Subsequent changes: apply via diff
    try {
      map.setStyle(style as maplibregl.StyleSpecification, { diff: true });
    } catch {
      try {
        map.setStyle(style as maplibregl.StyleSpecification);
      } catch {
        // Style is invalid — validation will catch this
      }
    }

    // Consume pending fly-to from loadNewStyle (URL/file load)
    const pending = useEditorStore.getState().pendingFlyTo;
    if (pending) {
      useEditorStore.setState({ pendingFlyTo: null });

      const applyFlyTo = () => {
        map.jumpTo({
          center: pending.center,
          zoom: pending.zoom,
          pitch: pending.pitch,
          bearing: pending.bearing,
        });
        compareMapRef.current?.getMap()?.jumpTo({
          center: pending.center,
          zoom: pending.zoom,
          pitch: pending.pitch,
          bearing: pending.bearing,
        });
      };

      if (map.loaded()) {
        applyFlyTo();
      } else {
        map.once("load", applyFlyTo);
      }
    }
  }, [style, styleVersion]);

  // Override compass button: if pitch is already 0, go to max pitch instead
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const handleLoad = () => {
      const compass = map.getContainer().querySelector<HTMLButtonElement>(
        ".maplibregl-ctrl-compass",
      );
      if (!compass) return;

      compass.addEventListener("click", (e) => {
        const currentPitch = map.getPitch();
        if (currentPitch < 1) {
          // Already flat — go to max pitch
          e.stopPropagation();
          e.preventDefault();
          map.easeTo({ pitch: MAX_PITCH, bearing: map.getBearing(), duration: 500 });
        }
        // Otherwise, the default NavigationControl behavior resets to 0
      }, true); // capture phase to intercept before NavigationControl
    };

    if (map.isStyleLoaded()) {
      handleLoad();
    } else {
      map.on("load", handleLoad);
    }
  }, []);

  /** Fly both main and comparison maps together */
  const flyBoth = (opts: maplibregl.FlyToOptions) => {
    mapRef.current?.getMap()?.flyTo(opts);
    compareMapRef.current?.getMap()?.flyTo(opts);
  };

  const easeBoth = (opts: maplibregl.EaseToOptions) => {
    mapRef.current?.getMap()?.easeTo(opts);
    compareMapRef.current?.getMap()?.easeTo(opts);
  };

  // Listen for "fly to initial view" commands from the editor
  useEffect(() => {
    return onFlyToInitialView(() => {
      const s = useEditorStore.getState().style;
      const c = Array.isArray(s.center) ? (s.center as number[]) : [...MAP_DEFAULTS.center];
      flyBoth({
        center: [(c[0] as number) ?? 0, (c[1] as number) ?? 0],
        zoom: (typeof s.zoom === "number" ? s.zoom : MAP_DEFAULTS.zoom) as number,
        pitch: (typeof s.pitch === "number" ? s.pitch : MAP_DEFAULTS.pitch) as number,
        bearing: (typeof s.bearing === "number" ? s.bearing : MAP_DEFAULTS.bearing) as number,
        duration: 1200,
      });
    });
  }, []);

  // Listen for "fly to zoom" commands
  useEffect(() => {
    return onFlyToZoom((zoom) => {
      easeBoth({ zoom, duration: 800 });
    });
  }, []);

  const handleMove = useCallback(
    (e: ViewStateChangeEvent) => {
      setViewport({
        zoom: e.viewState.zoom,
        center: [e.viewState.longitude, e.viewState.latitude],
        bearing: e.viewState.bearing,
        pitch: e.viewState.pitch,
      });
      // Sync comparison map
      if (!syncing.current && compareMapRef.current) {
        syncing.current = true;
        compareMapRef.current.getMap()?.jumpTo({
          center: [e.viewState.longitude, e.viewState.latitude],
          zoom: e.viewState.zoom,
          bearing: e.viewState.bearing,
          pitch: e.viewState.pitch,
        });
        syncing.current = false;
      }
    },
    [setViewport],
  );

  const compareReady = useRef(false);
  // Reset when comparison toggles off
  useEffect(() => {
    if (!showComparison) compareReady.current = false;
  }, [showComparison]);
  const handleCompareMove = useCallback(
    (e: ViewStateChangeEvent) => {
      // Skip sync during initial load — wait until user actually interacts
      if (!compareReady.current) return;
      if (!syncing.current && mapRef.current) {
        syncing.current = true;
        mapRef.current.getMap()?.jumpTo({
          center: [e.viewState.longitude, e.viewState.latitude],
          zoom: e.viewState.zoom,
          bearing: e.viewState.bearing,
          pitch: e.viewState.pitch,
        });
        setViewport({
          zoom: e.viewState.zoom,
          center: [e.viewState.longitude, e.viewState.latitude],
          bearing: e.viewState.bearing,
          pitch: e.viewState.pitch,
        });
        syncing.current = false;
      }
    },
    [setViewport],
  );

  const handleClick = useCallback(
    (e: maplibregl.MapMouseEvent) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const features = map.queryRenderedFeatures(e.point);
      const topFeature = features[0];
      if (topFeature?.layer?.id && !topFeature.layer.id.startsWith("__editor-")) {
        setSelectedLayerId(topFeature.layer.id);
        setEditorMode("sections");
        setActiveSection("layers");
      }
    },
    [setSelectedLayerId, setEditorMode, setActiveSection],
  );

  const initialPitch =
    typeof style.pitch === "number" && isFinite(style.pitch) ? style.pitch : MAP_DEFAULTS.pitch;
  const initialBearing =
    typeof style.bearing === "number" && isFinite(style.bearing) ? style.bearing : MAP_DEFAULTS.bearing;
  const rawCenter = Array.isArray(style.center) && style.center.length >= 2 ? style.center : null;
  const initialCenter = rawCenter && typeof rawCenter[0] === "number" && typeof rawCenter[1] === "number"
    ? rawCenter as [number, number]
    : MAP_DEFAULTS.center;
  const initialZoom =
    typeof style.zoom === "number" && isFinite(style.zoom) ? style.zoom : MAP_DEFAULTS.zoom;

  return (
    <div className="relative h-full w-full" data-testid="map-container">
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: (initialCenter[0] as number) ?? 0,
          latitude: (initialCenter[1] as number) ?? 20,
          zoom: initialZoom,
          pitch: initialPitch,
          bearing: initialBearing,
        }}
        mapStyle={style as maplibregl.StyleSpecification}
        onMove={handleMove}
        onClick={handleClick}
        dragRotate
        touchZoomRotate
        touchPitch
        maxPitch={MAX_PITCH}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-right" visualizePitch />
        <GeolocateControl position="top-right" />
      </Map>

      {/* Monitor toggle */}
      <button
        type="button"
        onClick={() => setShowMonitor((v) => !v)}
        className={`absolute top-3 left-3 z-20 flex h-7 w-7 items-center justify-center rounded-radius-md border text-[11px] shadow-sm transition-colors ${
          showMonitor
            ? "border-accent bg-accent text-accent-text"
            : "border-border-subtle bg-surface-raised text-text-muted hover:text-text-primary"
        }`}
        title={showMonitor ? "Hide monitor" : "Show monitor"}
      >
        ⊞
      </button>

      {showMonitor && <MonitorPanel mapRef={mapRef} />}

      {/* Before/after comparison — clipped full-size map for seamless divider */}
      {showComparison && originalStyle && (
        <>
          {/* Before map — full size, clipped to left 50% */}
          <div className="absolute inset-0 z-10 overflow-hidden" style={{ clipPath: "inset(0 50% 0 0)" }}>
            <Map
              ref={compareMapRef}
              initialViewState={{
                longitude: viewport.center[0],
                latitude: viewport.center[1],
                zoom: viewport.zoom,
                pitch: viewport.pitch,
                bearing: viewport.bearing,
              }}
              mapStyle={originalStyle as unknown as maplibregl.StyleSpecification}
              onMove={handleCompareMove}
              onLoad={() => {
                // Sync to the main map's ACTUAL viewport, not the store
                const mainMap = mapRef.current?.getMap();
                const cMap = compareMapRef.current?.getMap();
                if (cMap && mainMap) {
                  syncing.current = true;
                  cMap.jumpTo({
                    center: mainMap.getCenter(),
                    zoom: mainMap.getZoom(),
                    pitch: mainMap.getPitch(),
                    bearing: mainMap.getBearing(),
                  });
                  syncing.current = false;
                  // Now allow user-driven sync
                  compareReady.current = true;
                }
              }}
              dragRotate
              touchZoomRotate
              touchPitch
              maxPitch={MAX_PITCH}
              style={{ width: "100%", height: "100%" }}
            />
          </div>

          {/* Divider line */}
          <div className="absolute inset-y-0 left-1/2 z-20 w-0.5 bg-accent shadow-lg" />

          {/* Labels */}
          <div className="absolute top-3 left-1/4 z-20 -translate-x-1/2 rounded-radius-full bg-surface-overlay/90 px-3 py-1 text-[10px] font-medium text-text-secondary shadow-sm">
            Before
          </div>
          <div className="absolute top-3 left-3/4 z-20 -translate-x-1/2 rounded-radius-full bg-accent px-3 py-1 text-[10px] font-medium text-accent-text shadow-sm">
            After
          </div>
        </>
      )}
    </div>
  );
}
